// What a visitor sees on the explicit landing pages (ai_app_backend#1441), decided by the middleware from the request's
// country and three flags, all off by default. No browser dependencies: the middleware imports it.
//
// - `signed_out_blurred_pictures`: the galleries' items marked `nsfw` are small pre-blurred thumbnails
//   (`public/assets/static/signed-out/`, made by scripts/build_signed_out_showcase.mjs), so the full file's address is
//   never sent.
// - `signed_out_safe_countries`: in a country on the list (src/config/signedOutGatedCountries.js) or an unknown
//   one (`ZZ`), those items are a plain placeholder instead.
// - `signed_out_age_notice`: the 18+ notice (component/AgeNotice.jsx) on the explicit landing pages, once a visitor.
//
// The middleware answers in two cookies the page reads: SHOWCASE_COOKIE (`blurred` or `safe`; absent: the full
// gallery) and AGE_NOTICE_COOKIE (`1`: show the notice). With every flag off it sets neither, and removes one left from
// before. Signed-in people never stay on these pages (they go to /explore), so neither cookie concerns them.
import { SIGNED_OUT_GATED_COUNTRIES } from '../config/signedOutGatedCountries.js'

export const SHOWCASE_COOKIE = 'pc_showcase'
export const AGE_NOTICE_COOKIE = 'pc_age_notice'

export const FLAGS = Object.freeze({
  blurred: 'signed_out_blurred_pictures',
  countries: 'signed_out_safe_countries',
  ageNotice: 'signed_out_age_notice',
})

/** What Vercel sends, or the middleware uses, when it can't tell the country. */
export const UNKNOWN_COUNTRY = 'ZZ'

export const GATED_COUNTRIES = Object.freeze([...SIGNED_OUT_GATED_COUNTRIES])

/** The landing pages whose galleries show items marked `nsfw`, with or without a language prefix (/de/nsfw-ai). */
const GALLERY_PAGES =
  /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:ai-porn|nsfw-ai|ai-porn-video-generator)$/

/** The explicit landing pages the 18+ notice is shown before. */
const NOTICE_PAGES =
  /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:ai-porn|nsfw-ai|nsfw-ai-chat|ai-porn-video-generator|ai-hentai|gay-ai-porn)$/

/** Whether the gallery on this (normalised) path takes the signed-out variants. */
export function isGalleryPage(path) {
  return GALLERY_PAGES.test(path)
}

/** Whether the 18+ notice belongs on this (normalised) path. */
export function isNoticePage(path) {
  return NOTICE_PAGES.test(path)
}

/** Whether a country (two letters as Vercel sends them, or missing) is on the list or unknown. */
export function isGatedCountry(country) {
  const code = (country || UNKNOWN_COUNTRY).toUpperCase()
  return code === UNKNOWN_COUNTRY || GATED_COUNTRIES.includes(code)
}

/** The gallery variant: 'safe', 'blurred', or null for the full gallery. */
export function chooseShowcase({ country, blurred, countries }) {
  if (countries && isGatedCountry(country)) return 'safe'
  if (blurred) return 'blurred'
  return null
}

/** The variant a cookie value names, or null (the full gallery) for anything else. */
export function showcaseVariant(value) {
  return value === 'blurred' || value === 'safe' ? value : null
}

const REMEMBER_MS = 30 * 24 * 60 * 60 * 1000

/** Whether an 18+ answer stored at `stored` (ms since 1970, as text) still holds at `now`: for 30 days. */
export function rememberedAnswer(stored, now = Date.now()) {
  const at = Number(stored)
  return Number.isFinite(at) && at > 0 && now - at < REMEMBER_MS
}
